import { createAction, createReducer } from 'deox';

import { FeatureMap } from 'state/tenant';

import { set, lensPath, pick } from 'ramda';

import { resetApplication } from './resetApplication';
export const RELATIONSHIPS = {
  T: 'Aunt',
  B: 'Brother',
  C: 'Cousin',
  D: 'Daughter',
  F: 'Father',
  G: 'Grandparent',
  H: 'Husband',
  M: 'Mother',
  O: 'Other',
  R: 'Sister',
  S: 'Son',
  U: 'Uncle',
  W: 'Wife',
};

export type Relationship = keyof typeof RELATIONSHIPS;

export interface AuthBuyer {
  firstName: string | null;
  middleInitial: string | null;
  lastName: string | null;
  relationship: Relationship | null;
  dateOfBirth: string | null;
}

export interface JointApplicant {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  mobilePhone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  income: string | null;
  ssn: string | null;
  sameAddressAsPrimaryApplicant: boolean | null;
}

export interface CreditApplication {
  cardChoice: number | null;
  firstName: string | null;
  lastName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  dateOfBirth: string | null;
  ssn: string | null;
  emailAddress: string | null;
  mobilePhone: string | null;
  alternativePhone: string | null;
  income: string | null;
  intendedPurchaseAmount: string | null;
  referralCode: string | null;
  clientVariable1: string | null;
  clientVariable2: string | null;
  clientVariable3: string | null;
  clientVariable4: string | null;

  authBuyer: AuthBuyer | null;
  accountAssure: boolean;
  jointApplicant: JointApplicant | null;

  middleInitial: string | null; // The new form doesn't include middle initial.
  clerkMode: string | null;
  storeNumber: string | null;
  associateId: string | null;

  mcid: string | null;

  category: string | null;
  cardType: string | null;
  cartAmount: number | null;
  checkoutAmount: number | null;
  correlationData: string | null;
  customerNumber: string | null;
  location: string | null;
  loyaltyNumber: string | null;
  productAmount: number | null;
  sku: string | null;

  productChoiceKey: string | null;

  studentApplication: boolean | null;
  optInOut: boolean | null;
}

export interface LockedFields {
  ssn: boolean;
  mobilePhone: boolean;
  dateOfBirth: boolean;
}

export interface CreditApplicationState {
  entity: CreditApplication;
  lockedFields: LockedFields;
}

export const defaultState: CreditApplicationState = {
  entity: {
    cardChoice: null,

    firstName: null,
    lastName: null,
    address1: null,
    address2: null,
    city: null,
    state: null,
    zip: null,
    dateOfBirth: null,
    ssn: null,
    emailAddress: null,

    mobilePhone: null,
    alternativePhone: null,

    income: null,
    intendedPurchaseAmount: null,
    referralCode: null,

    studentApplication: null,
    optInOut: false,

    clientVariable1: null,
    clientVariable2: null,
    clientVariable3: null,
    clientVariable4: null,

    authBuyer: null,
    accountAssure: false,
    jointApplicant: null,

    // Adobe Marketing Cloud ID
    mcid: null,
    storeNumber: null,

    category: null,
    cardType: null,
    cartAmount: null,
    checkoutAmount: null,
    correlationData: null,
    customerNumber: null,
    location: null,
    loyaltyNumber: null,
    productAmount: null,
    sku: null,

    productChoiceKey: null,

    // TODO: figure out requirements for these
    middleInitial: null, // The new form doesn't include middle initial.
    clerkMode: null,
    associateId: null,
  },
  lockedFields: {
    ssn: false,
    mobilePhone: false,
    dateOfBirth: false,
  },
};

const prefillFields = {
  entity: [
    'firstName',
    'lastName',
    'address1',
    'address2',
    'city',
    'state',
    'zip',
    'dateOfBirth',
    'ssn',
    'emailAddress',
    'mobilePhone',
    'alternativePhone',
    'income',
    'authBuyer',
    'accountAssure',
  ],
  lockedFields: ['ssn', 'mobilePhone', 'dateOfBirth'],
};

export const mergeApplication = createAction(
  'MERGE_APPLICATION',
  resolve => (data: Partial<CreditApplication>) => resolve(data),
);

export const lockFields = createAction(
  'LOCK_FIELDS',
  resolve => (data: Partial<LockedFields>) => resolve(data),
);

export const setClientVariable = createAction(
  'SET_CLIENT_VARIABLE',
  /**
   * @param clientVariableNumber the clientVariable number to set
   * @param string the value to set
   * @param position The 0-index position of the in the string to set the
   * value. The string will be padded if position is greater than its length.
   * If the position is not provided, the entire string wil be replace with value.
   * NOTE: ADS specification may indicate the position indexing from 1.
   */
  resolve => (
    clientVariableNumber: 1 | 2 | 3 | 4,
    value: string,
    position?: number,
  ) => resolve({ clientVariableNumber, value, position }),
);

export const clearPostPrefillFields = createAction('CLEAR_POST_PREFILL_FIELDS');

const splice = (str: string, newStr: string, position: number): string => {
  return (
    str.slice(0, position).padEnd(position) +
    newStr +
    str.slice(position + newStr.length)
  );
};

export const creditApplicationReducer = createReducer(
  defaultState,
  handleAction => [
    handleAction(mergeApplication, (state, { payload: data }) => {
      return {
        ...state,
        entity: {
          ...state.entity,
          ...data,
        },
      };
    }),

    handleAction(lockFields, (state, { payload: lockedFields }) => {
      return {
        ...state,
        lockedFields: {
          ...state.lockedFields,
          ...lockedFields,
        },
      };
    }),

    handleAction(
      setClientVariable,
      (state, { payload: { clientVariableNumber, value, position } }) => {
        const field = `clientVariable${clientVariableNumber}` as (
          | 'clientVariable1'
          | 'clientVariable2'
          | 'clientVariable3'
          | 'clientVariable4');

        if (position !== undefined) {
          const currentValue = state.entity[field] || '';
          value = splice(currentValue, value, position);
        }

        return set(lensPath(['entity', field]), value, state);
      },
    ),

    handleAction(clearPostPrefillFields, state => {
      return {
        ...state,
        entity: {
          ...state.entity,
          ...pick(prefillFields.entity, defaultState.entity),
        },
        lockedFields: {
          ...state.lockedFields,
          ...pick(prefillFields.lockedFields, defaultState.lockedFields),
        },
      };
    }),

    handleAction(resetApplication, () => defaultState),
  ],
);

export interface MountedState {
  creditApplication: ReturnType<typeof creditApplicationReducer>;
}

export const selectCreditApplication = (
  state: MountedState,
): CreditApplication => state.creditApplication.entity;

/**
 * @returns whether the user optIn or optOut of being considered for multiple
 * products. Returns null if the tenant doesn't configure this feature.
 */
export const isOptInOrOptOut = (
  creditApplication: CreditApplication,
  tenant: { enabledFeatures: Pick<FeatureMap, 'optInOut'> },
): 'optIn' | 'optOut' | null => {
  const {
    enabledFeatures: { optInOut },
  } = tenant;

  if (!optInOut) {
    return null;
  }

  const { type } = optInOut;

  const isOptIn =
    (type === 'optIn' && Boolean(creditApplication.optInOut)) ||
    (type === 'optOut' && !creditApplication.optInOut);

  return isOptIn ? 'optIn' : 'optOut';
};

export const selectLockedFields = (state: MountedState): LockedFields =>
  state.creditApplication.lockedFields;

export default creditApplicationReducer;
